import { CalendarMonth as CalendarIcon } from "@mui/icons-material";
import { Box, Typography, IconButton } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";

import { TimeRange } from "helpers/dateRangeHelpers";

interface ChartHeaderProps {
  icon: React.ReactElement;
  title: string;
  colors: { primary: { main: string } };
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (open: boolean) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTimeRange: (range: TimeRange) => void;
}

/**
 * Reusable chart header component with title, icon, and date picker
 */
const ChartHeader: React.FC<ChartHeaderProps> = ({
  icon,
  title,
  colors,
  isDatePickerOpen,
  setIsDatePickerOpen,
  anchorEl,
  setAnchorEl,
  selectedDate,
  setSelectedDate,
  setSelectedTimeRange,
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {React.cloneElement(icon, {
          sx: { color: colors.primary.main, fontSize: 22 },
        })}
        <Typography
          variant="body1"
          fontWeight={500}
          color="text.primary"
          sx={{ fontSize: "0.875rem" }}
        >
          {title}
        </Typography>
      </Box>

      {/* Calendar Date Picker */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          open={isDatePickerOpen}
          onClose={() => {
            setIsDatePickerOpen(false);
            setAnchorEl(null);
          }}
          value={selectedDate}
          onChange={(newValue) => {
            setSelectedDate(newValue);
            setSelectedTimeRange("CUSTOM");
            setIsDatePickerOpen(false);
            setAnchorEl(null);
          }}
          maxDate={new Date()}
          slotProps={{
            popper: {
              anchorEl: anchorEl,
              placement: "bottom-end",
              sx: {
                zIndex: 1300,
              },
            },
            textField: {
              sx: { display: "none" },
            },
          }}
        />
        <IconButton
          ref={(el) => setAnchorEl(el)}
          onClick={(event) => {
            setIsDatePickerOpen(true);
            setAnchorEl(event.currentTarget);
          }}
        >
          <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
        </IconButton>
      </LocalizationProvider>
    </Box>
  );
};

export default ChartHeader;
